import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { Input, Form, Row, Col, Space, Button, Tooltip, Modal, Flex, InputNumber, Select, Upload, Divider, notification } from 'antd';
import type { UploadFile } from 'antd/es/upload';
import { RollbackOutlined, QuestionCircleOutlined, TruckFilled } from '@ant-design/icons';

import CargoCarrier from '@components/cargo-carrier/cargo-carrier';
import UploadDocument from '@components/upload-document/upload-document';

import PhoneInputFormItem from '@controls/phone-input-form-item/phone-input-form-item';
import FormHeader from '@controls/form-header/form-header';

import { exception } from '@extensions/notification';
import { userLoaded } from '@store/actions';
import { serverFetch } from '@flp/server';
import { getEnumList } from '@extensions/utils';

import { useAppDispatch, useAppSelector } from '@store/hooks';

import { ITruck } from '@entities/truck';
import { IUserSession } from '@entities/user-session';

import { TruckType } from '@enums/truck-type';
import { TruckStatus, enumLabel } from '@enums/truck-status';
import { Permission, hasPermission } from '@enums/permission';
import { Currency, enumSign as currencySign, enumLabel as currencyLabel } from '@enums/currency';
import { IEnumItem } from '@enums/enum-item';
import { DocumentReferenceType } from '@enums/document-reference-type';
import { DocumentType } from '@enums/document-type';
import { CargoCarrierType } from '@enums/cargo-carrier-type';

dayjs.extend(utc);

const Truck = () => {
    const { TextArea } = Input;

    const userSession = useAppSelector<IUserSession>((s) => s.userSession);

    const { journalId, truckId } = useParams();
    const [api, contextHolder] = notification.useNotification();

    const d = useAppDispatch();
    const navigate = useNavigate();

    const [modal, modalContextHolder] = Modal.useModal();
    const [truck, setTruck] = useState<ITruck>();
    const [isViewOnly, setIsViewOnly] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [currencies] = useState<Array<IEnumItem>>(getEnumList(Currency, currencyLabel));

    useEffect(() => {
        let cleanup = false;

        const fetchData = async () => {
            setLoading(true);

            const promises = [];

            if (truckId) {
                promises.push(
                    await serverFetch(`trucks/${truckId}`, { method: 'GET' })
                        .then((data) => {
                            return data;
                        })
                        .catch((ex) => {
                            exception(api, 'Ошибка получения машины', ex, () => d(userLoaded(undefined)));
                        }),
                );
            }

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                if (truckId) {
                    setTruck(result[0][0]);
                } else {
                    setTruck({
                        id: undefined,
                        number: undefined,
                        departureOn: undefined,
                        type: TruckType.Delivery,
                        status: TruckStatus.New,
                        charteringCurrency: Currency.Rub,
                    });
                }

                setLoading(false);
            });
        };

        fetchData();

        return () => {
            cleanup = true;
        };
    }, []);

    useEffect(() => {
        setIsViewOnly(!journalId || truck?.status === TruckStatus.Finished);
    }, [truck]);

    const onRevertOnway = () => {
        if (!truckId) return;

        serverFetch(`trucks/delivery/${truckId}/revertonway`, { method: 'POST' })
            .then(() => {
                navigate(-1);
            })
            .catch((ex) => {
                setLoading(false);
                exception(api, 'Ошибка изменения статуса', ex, () => d(userLoaded(undefined)));
            });
    };

    const onCompleteShipment = () => {
        setLoading(true);

        serverFetch(`warehouse/completeshipment/${journalId}`, { method: 'POST' })
            .then(() => {
                setLoading(false);
                navigate(-1);
            })
            .catch((ex) => {
                setLoading(false);
                exception(api, 'Ошибка отправки машины', ex, () => d(userLoaded(undefined)));
            });
    };

    const onFinish = () => {
        if (!truck) return;

        setLoading(true);

        const result = { ...truck, documents: [], internalDocuments: [], journalId: journalId };

        serverFetch(`trucks`, { method: truckId ? 'PUT' : 'POST', bodyData: result })
            .then(() => {
                setLoading(false);
                navigate(-1);
            })
            .catch((ex) => {
                setLoading(false);
                exception(api, 'Ошибка сохранения машины', ex, () => d(userLoaded(undefined)));
            });
    };

    return (
        <>
            <Row>
                <FormHeader title={`${truckId ? 'Управление машиной' : 'Добавить машину'}`} />
            </Row>

            {truck && (
                <Row>
                    <Col span={10}>
                        <Form colon={false} labelCol={{ span: 8 }} wrapperCol={{ span: 16 }} onFinish={onFinish}>
                            <Form.Item
                                initialValue={truck?.number}
                                required={true}
                                label='Номер машины'
                                name='number'
                                rules={[
                                    { required: true, message: 'Укажите номер машины' },
                                    {
                                        validator: async (_, value: string) => {
                                            if (value && value.length < 3) return Promise.reject('Укажите правильный номер автомобиля');
                                            return Promise.resolve();
                                        },
                                    },
                                ]}
                                wrapperCol={{ span: 5 }}
                            >
                                <Input
                                    disabled={isViewOnly}
                                    autoFocus
                                    min={3}
                                    onChange={(data) => {
                                        if (!truck) return;

                                        setTruck({ ...truck, number: data.target.value });
                                    }}
                                />
                            </Form.Item>
                            <Form.Item initialValue={truck?.brand} label='Тип, марка' name='brand'>
                                <Input
                                    disabled={isViewOnly}
                                    autoFocus
                                    min={3}
                                    onChange={(data) => {
                                        if (!truck) return;

                                        setTruck({ ...truck, brand: data.target.value });
                                    }}
                                />
                            </Form.Item>
                            <Form.Item label='Статус' wrapperCol={{ span: 8 }}>
                                <Space.Compact style={{ width: '100%' }}>
                                    <Input disabled={true} defaultValue={enumLabel(truck?.status)} />
                                    {hasPermission(userSession.permissions, Permission.ManageDeliveryStatus) &&
                                        truck.status === TruckStatus.Finished && (
                                            <Tooltip title='Вернуть статус "В Пути"'>
                                                <Button
                                                    type='primary'
                                                    icon={<RollbackOutlined />}
                                                    onClick={() => {
                                                        modal.confirm({
                                                            title: `Изменить статус машины на "В Пути"?`,
                                                            okType: 'primary',
                                                            icon: <QuestionCircleOutlined />,
                                                            okText: 'ОК',
                                                            cancelText: 'Отмена',
                                                            onOk: () => {
                                                                onRevertOnway();
                                                            },
                                                        });
                                                    }}
                                                />
                                            </Tooltip>
                                        )}

                                    {hasPermission(userSession.permissions, Permission.ManageDeliveryStatus) &&
                                        truck.status === TruckStatus.New && (
                                            <Tooltip title='Отправить машину'>
                                                <Button
                                                    type='primary'
                                                    icon={<TruckFilled />}
                                                    onClick={() => {
                                                        modal.confirm({
                                                            title: `Изменить статус машины на "В Пути"?`,
                                                            okType: 'primary',
                                                            icon: <QuestionCircleOutlined />,
                                                            okText: 'ОК',
                                                            cancelText: 'Отмена',
                                                            onOk: () => {
                                                                onCompleteShipment();
                                                            },
                                                        });
                                                    }}
                                                />
                                            </Tooltip>
                                        )}
                                </Space.Compact>
                            </Form.Item>

                            <Form.Item label='Перевозчик'>
                                <CargoCarrier
                                    defaultCargoCarrierId={truck.cargoCarrierId}
                                    type={CargoCarrierType.Truck}
                                    disabled={isViewOnly}
                                    api={api}
                                    onSelect={(value: string | undefined) => {
                                        setTruck({ ...truck, cargoCarrierId: value });
                                    }}
                                    serverFetch={serverFetch}
                                />
                            </Form.Item>

                            <Form.Item initialValue={truck?.driverName} label='ФИО водителя' name='driverName'>
                                <Input
                                    disabled={isViewOnly}
                                    onChange={(data) => {
                                        if (!truck) return;

                                        setTruck({ ...truck, driverName: data.target.value });
                                    }}
                                />
                            </Form.Item>

                            <PhoneInputFormItem
                                value={truck?.driverPhone}
                                name='driverPhone'
                                label='Телефон водителя'
                                required={true}
                                disabled={isViewOnly}
                                onChange={(value) => {
                                    setTruck({ ...truck, driverPhone: value });
                                }}
                            />

                            <Form.Item initialValue={truck?.driverDetails} label='Паспорт водителя' name='driverDetails'>
                                <TextArea
                                    rows={3}
                                    disabled={isViewOnly}
                                    onChange={(data) => {
                                        if (!truck) return;

                                        setTruck({ ...truck, driverDetails: data.target.value });
                                    }}
                                />
                            </Form.Item>

                            <Form.Item initialValue={truck?.trackingUrl} label='Трекинг URL' name='trackingUrl'>
                                <Input
                                    disabled={isViewOnly}
                                    onChange={(data) => {
                                        if (!truck) return;

                                        setTruck({ ...truck, trackingUrl: data.target.value });
                                    }}
                                />
                            </Form.Item>
                            <Form.Item label='Комментарий' name='comment' initialValue={truck?.comment}>
                                <TextArea
                                    disabled={isViewOnly}
                                    rows={4}
                                    onChange={(data) => {
                                        setTruck({ ...truck, comment: data.target.value });
                                    }}
                                />
                            </Form.Item>

                            {hasPermission(userSession.permissions, Permission.ManageCostPriceTruck) && (
                                <>
                                    <Form.Item label='Доставка' style={{ marginBottom: 0 }}>
                                        <Flex gap='small'>
                                            <Form.Item name='charteringAmount' initialValue={truck.charteringAmount}>
                                                <InputNumber
                                                    precision={2}
                                                    decimalSeparator=','
                                                    min={0}
                                                    onChange={(value: number | null) => {
                                                        setTruck({ ...truck, charteringAmount: value });
                                                    }}
                                                    addonAfter={
                                                        <Select
                                                            defaultValue={truck.charteringCurrency}
                                                            onChange={(value: number) => {
                                                                setTruck({ ...truck, charteringCurrency: value });
                                                            }}
                                                            options={currencies.map((c) => {
                                                                return {
                                                                    value: c.value,
                                                                    label: c.label,
                                                                };
                                                            })}
                                                        />
                                                    }
                                                />
                                            </Form.Item>
                                            <Form.Item name='charteringCrossRate' initialValue={truck.charteringCrossRate}>
                                                <InputNumber
                                                    placeholder='Кросс-курс'
                                                    precision={4}
                                                    decimalSeparator=','
                                                    min={0}
                                                    onChange={(value: number | null) => {
                                                        setTruck({ ...truck, charteringCrossRate: value });
                                                    }}
                                                    addonAfter={currencySign(Currency.Usd)}
                                                />
                                            </Form.Item>
                                            <Form.Item>
                                                <InputNumber
                                                    value={(truck.charteringAmount ?? 0) * (truck.charteringCrossRate ?? 0)}
                                                    placeholder='Итого'
                                                    precision={2}
                                                    decimalSeparator=','
                                                    disabled={true}
                                                    addonAfter={currencySign(Currency.Usd)}
                                                />
                                            </Form.Item>
                                        </Flex>
                                    </Form.Item>
                                </>
                            )}

                            <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
                                <Space size={'small'} style={{ float: 'right' }}>
                                    <Button type='text' onClick={() => navigate(-1)}>
                                        Закрыть
                                    </Button>
                                    {!isViewOnly && (
                                        <Button type='primary' htmlType='submit' loading={loading}>
                                            Сохранить
                                        </Button>
                                    )}
                                </Space>
                            </Form.Item>
                        </Form>
                    </Col>

                    {truck.id && (
                        <>
                            <Col span={8} offset={1}>
                                <Divider titlePlacement='left'> Документы для клиентов</Divider>
                                <Form.Item
                                    wrapperCol={{ span: 24 }}
                                    style={{ textAlign: 'left', marginTop: 10, border: '1px solid #d9d9d9', borderRadius: 5, padding: 5 }}
                                >
                                    <UploadDocument
                                        documentType={DocumentType.Other}
                                        referenceId={truck.id}
                                        referenceType={DocumentReferenceType.Truck}
                                        files={truck.documents}
                                        api={api}
                                        onChange={(fileList: Array<UploadFile>) => {
                                            if (truck) {
                                                setTruck({ ...truck, documents: fileList });
                                            }
                                        }}
                                        serverFetch={serverFetch}
                                    />
                                </Form.Item>

                                <Divider titlePlacement='left' style={{ marginTop: 40, backgroundColor: 'var(--main-red)' }}>
                                    Документы для внутреннего использования
                                </Divider>
                                <Form.Item
                                    wrapperCol={{ span: 24 }}
                                    style={{ textAlign: 'left', marginTop: 10, border: '1px solid #d9d9d9', borderRadius: 5, padding: 5 }}
                                >
                                    <UploadDocument
                                        documentType={DocumentType.InternalUseOnly}
                                        referenceId={truck.id}
                                        referenceType={DocumentReferenceType.Truck}
                                        files={truck.internalDocuments}
                                        api={api}
                                        onChange={(fileList: Array<UploadFile>) => {
                                            if (truck) {
                                                setTruck({ ...truck, internalDocuments: fileList });
                                            }
                                        }}
                                        serverFetch={serverFetch}
                                    />
                                </Form.Item>
                            </Col>
                        </>
                    )}
                </Row>
            )}

            {contextHolder}
            {modalContextHolder}
        </>
    );
};

export default Truck;
